import React, { useEffect } from 'react';
import VECIErrorMessage from './VECIErrorMessage';

const VECIActiveQuestion = ({
  question,
  register,
  watch,
  setValue,
  onNext,
  elapsed,
  errors,
  strongestCompetitorName,
  isValidating,
  hideNotes,
}) => {
  if (!question) return null;

  const isContext = question.type !== 'scale';
  const fieldName = isContext
    ? `answers.${question.questionCode}.optionValue`
    : `answers.${question.questionCode}.score`;
  const noteField = `answers.${question.questionCode}.note`;
  const shownAtField = `answers.${question.questionCode}.itemShownAt`;
  const answeredAtField = `answers.${question.questionCode}.itemAnsweredAt`;

  // Catat timestamp saat pertanyaan pertama kali ditampilkan kepada PO
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!watch(shownAtField)) {
      setValue(shownAtField, new Date().toISOString());
    }
  }, []);

  // Handler saat user memilih jawaban — catat itemAnsweredAt
  const handleAnswer = (val) => {
    setValue(fieldName, val);
    if (!watch(answeredAtField)) {
      setValue(answeredAtField, new Date().toISOString());
    }
  };

  const currentOptions = Array.isArray(question.options)
    ? question.options
    : typeof question.options === 'string'
      ? JSON.parse(question.options)
      : [];

  return (
    <div className='card border-0 shadow-sm rounded-4 p-4 p-md-5 animate-slide-up bg-white'>
      <div className='mb-4'>
        {process.env.NODE_ENV !== 'production' && (
          <div className='d-flex align-items-center mb-3'>
            <span className='badge bg-primary px-3 py-2 rounded-pill me-2'>
              {question.questionCode}
            </span>
            <span className='text-muted small'>
              <i className='bi bi-clock me-1'></i> {elapsed} detik di pertanyaan
              ini
            </span>
          </div>
        )}
        <h4 className='fw-bold text-dark mb-2'>{question.title}</h4>
        <p className='text-muted fs-5 mb-3'>
          {question.description || question.desc}
        </p>

        {/* Subtitle: Nama pesaing terkuat sebagai konteks frozen (hanya untuk soal skala OP/AD/MP) */}
        {!isContext && strongestCompetitorName && (
          <div
            className='d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 mb-1'
            style={{
              background: 'rgba(13,110,253,0.07)',
              border: '1px dashed rgba(13,110,253,0.3)',
            }}
          >
            <i className='bi bi-crosshair text-primary small'></i>
            <span className='small text-primary fw-semibold'>
              Dibandingkan dengan:&nbsp;
            </span>
            <span className='small fw-bold text-dark'>
              {strongestCompetitorName}
            </span>
          </div>
        )}
      </div>

      <div className='mb-5'>
        {question.type === 'scale' ? (
          <div className='row g-3'>
            {[1, 2, 3, 4, 5].map((num) => (
              <div
                key={num}
                className='col'
              >
                <input
                  type='radio'
                  className='btn-check'
                  id={`btn-${question.questionCode}-${num}`}
                  value={num}
                  {...register(fieldName, {
                    required: 'Pilih salah satu skor',
                    onChange: (e) => handleAnswer(e.target.value),
                  })}
                  checked={watch(fieldName) == num}
                />
                <label
                  className={`btn btn-outline-primary w-100 py-3 rounded-4 border-2 fw-bold fs-4 transition-all ${watch(fieldName) == num ? 'shadow-lg' : ''}`}
                  htmlFor={`btn-${question.questionCode}-${num}`}
                >
                  {num}
                  <small
                    className='d-none d-md-block lh-sm opacity-75'
                    style={{ fontSize: '10px' }}
                  >
                    {num === 1
                      ? 'Sangat Buruk'
                      : num === 2
                        ? 'Buruk'
                        : num === 3
                          ? 'Setara'
                          : num === 4
                            ? 'Lebih Baik'
                            : 'Sangat Baik'}
                  </small>
                </label>
              </div>
            ))}
          </div>
        ) : question.type === 'text' || question.type === 'paragraph' ? (
          <div className='vstack gap-2'>
            {question.type === 'text' ? (
              <input
                type='text'
                className={`form-control border-2 rounded-4 bg-light py-3 px-4 ${watch(fieldName) ? 'border-primary' : ''}`}
                placeholder='Ketik jawaban Anda di sini...'
                {...register(fieldName, {
                  required: 'Jawaban ini wajib diisi',
                  onChange: (e) => handleAnswer(e.target.value),
                })}
              />
            ) : (
              <textarea
                className={`form-control border-2 rounded-4 bg-light py-3 px-4 ${watch(fieldName) ? 'border-primary' : ''}`}
                rows='4'
                placeholder='Ketik jawaban Anda secara lengkap di sini...'
                {...register(fieldName, {
                  required: 'Jawaban ini wajib diisi',
                  onChange: (e) => handleAnswer(e.target.value),
                })}
              ></textarea>
            )}
          </div>
        ) : (
          <div className='vstack gap-2'>
            {currentOptions.map((opt, idx) => (
              <div
                key={opt.value}
                className='position-relative'
              >
                <input
                  type='radio'
                  className='btn-check'
                  id={`btn-${question.questionCode}-${opt.value}`}
                  value={opt.value}
                  {...register(fieldName, {
                    required: 'Pilih salah satu jawaban',
                    onChange: (e) => handleAnswer(e.target.value),
                  })}
                  checked={watch(fieldName) == opt.value}
                />
                <label
                  className={`btn btn-outline-primary w-100 text-start py-3 px-4 rounded-4 border-2 fs-6 transition-all ${watch(fieldName) == opt.value ? 'shadow-sm' : ''}`}
                  htmlFor={`btn-${question.questionCode}-${opt.value}`}
                >
                  <div className='d-flex align-items-center'>
                    <span
                      className={`me-3 rounded-circle d-flex align-items-center justify-content-center border-2 ${watch(fieldName) == opt.value ? 'bg-primary text-white border-primary' : 'text-primary border-primary'}`}
                      style={{
                        width: '24px',
                        height: '24px',
                        fontSize: '12px',
                      }}
                    >
                      {String.fromCharCode(65 + idx)}
                    </span>
                    {opt.label}
                  </div>
                </label>
              </div>
            ))}
          </div>
        )}
        <VECIErrorMessage
          error={
            isContext
              ? errors?.answers?.[question.questionCode]?.optionValue
              : errors?.answers?.[question.questionCode]?.score
          }
        />
      </div>

      {!(
        hideNotes ||
        question.type === 'text' ||
        question.type === 'paragraph' ||
        ['O1', 'O2', 'O3', 'O4'].includes(question.questionCode)
      ) && (
        <div className='mb-4'>
          <label className='form-label fw-semibold text-muted small text-uppercase'>
            <i className='bi bi-pencil-square me-2'></i> Catatan / Alasan
            Jawaban
          </label>
          <textarea
            {...register(noteField, {
              required: 'Berikan alasan singkat untuk jawaban Anda',
            })}
            className={`form-control border-2 rounded-4 bg-light ${errors?.answers?.[question.questionCode]?.note ? 'is-invalid border-danger' : ''}`}
            rows='3'
            placeholder='Berikan context tambahan kenapa Ibu/Bapak memilih skor tersebut...'
          ></textarea>
          <VECIErrorMessage
            error={errors?.answers?.[question.questionCode]?.note}
          />
        </div>
      )}

      <div className='text-end'>
        <button
          type='button'
          className='btn btn-primary px-5 rounded-pill shadow'
          onClick={onNext}
          disabled={isValidating}
        >
          {isValidating ? (
            <>
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              ></span>
              Validating...
            </>
          ) : (
            <>
              Simpan & Lanjut <i className='bi bi-arrow-right-circle ms-2'></i>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VECIActiveQuestion;
